import React, { useCallback, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { GetHighlightStyle } from "./utils/GetHighlightStyle";
import ArrowConnection from "./ArrowConnection";

const ImageViewerWithHighlights = ({
  imageUrl,
  fields,
  originalWidth,
  originalHeight,
  onFieldClick,
  selectedKey,
  fieldRefs,
  inputRefs,
  showHighlights,
  setShowHighlights,
  noCoordinates
}) => {
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [arrowPosition, setArrowPosition] = useState(null);

  // Calculate scale once image is loaded
  useEffect(() => {
    if (imageRef.current) {
      const { clientWidth, clientHeight } = imageRef.current;
      setScale({
        x: clientWidth / originalWidth,
        y: clientHeight / originalHeight,
      });
    }
  }, [originalWidth, originalHeight, imageUrl]);

  const updateArrowPosition = useCallback(() => {
    const inputElement = selectedKey ? inputRefs.current[selectedKey] : null;
    const container = containerRef.current;

    if (!selectedKey || !inputElement || !container) {
      setArrowPosition(null);
      return;
    }

    const field = fields.find((field) => field.commonKeys.includes(selectedKey));

    if (!field) {
      setArrowPosition(null);
      return;
    }

    const highlightElement = fieldRefs.current[field.key];

    if (!highlightElement) {
      setArrowPosition(null);
      return;
    }

    // Force a reflow to ensure positions are up to date after smooth scrolls.
    void highlightElement.offsetHeight;

    const inputRect = inputElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const highlightRect = highlightElement.getBoundingClientRect();
    const displayResultContainer = inputElement.closest('.display-result-section') || document.querySelector('.display-result-section');
    const displayResultRect = displayResultContainer?.getBoundingClientRect();

    const isInputVisible = displayResultRect &&
      inputRect.top >= displayResultRect.top &&
      inputRect.bottom <= displayResultRect.bottom;

    const isHighlightVisible =
      highlightRect.top >= containerRect.top &&
      highlightRect.bottom <= containerRect.bottom;

    if (!isInputVisible || !isHighlightVisible) {
      setArrowPosition(null);
      return;
    }

    const inputIsRightOfHighlight = inputRect.left >= highlightRect.right;

    setArrowPosition({
      startX: inputIsRightOfHighlight ? inputRect.left : inputRect.right,
      startY: inputRect.top + (inputRect.height / 2),
      endX: inputIsRightOfHighlight ? highlightRect.right : highlightRect.left,
      endY: highlightRect.top + highlightRect.height / 2
    });
  }, [selectedKey, inputRefs, fieldRefs, fields]);

  useEffect(() => {
    // Update arrow position immediately when selectedKey changes
    updateArrowPosition();

    // Also update after a short delay to ensure DOM has settled
    const timeoutId = setTimeout(updateArrowPosition, 100);
    return () => clearTimeout(timeoutId);
  }, [selectedKey, updateArrowPosition]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateArrowPosition();
    };

    // Add scroll listener to the image container
    container.addEventListener("scroll", handleScroll);

    // Add scroll listener to the display result section
    const displayResultSection = selectedKey
      ? inputRefs.current[selectedKey]?.closest('.display-result-section') || document.querySelector('.display-result-section')
      : document.querySelector('.display-result-section');
    if (displayResultSection) {
      displayResultSection.addEventListener("scroll", handleScroll);
    }
    window.addEventListener("resize", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (displayResultSection) {
        displayResultSection.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", handleScroll);
    };
  }, [selectedKey, inputRefs, updateArrowPosition]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="relative w-full h-full overflow-auto">
        {/* tool bar */}
        <div className="sticky top-4 left-0 z-50 w-fit mx-auto bg-white/95 backdrop-blur-sm shadow-lg rounded-full border border-gray-200 px-6 py-2 flex items-center justify-center gap-4 select-none">
          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={() => setZoom(1)}
              disabled={zoom === 1}
              className={`text-sm font-medium transition ${zoom === 1
                ? "text-gray-700 cursor-default"
                : "text-indigo-600 hover:text-indigo-700"
                }`}
            >
              {(zoom * 100).toFixed(0)}%
            </button>
            <button
              onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Divider and Highlights toggle */}
          {!noCoordinates && (
            <>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showHighlights}
                    onChange={() => setShowHighlights((prev) => !prev)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Show highlights</span>
                </label>
              </div>
            </>
          )}
        </div>

        {/* Zoom wrapper */}
        <div className="flex justify-center">
          <div
            style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
          >
            <div
              className="relative w-full max-w-[800px]"
              style={{ aspectRatio: `${originalWidth} / ${originalHeight}` }}
            >
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Document"
                className="w-full h-full object-contain"
                onLoad={() => {
                  if (imageRef.current) {
                    const { clientWidth, clientHeight } = imageRef.current;
                    setScale({
                      x: clientWidth / originalWidth,
                      y: clientHeight / originalHeight,
                    });
                  }
                }}
              />

              {showHighlights && scale &&
                fields.map((field, i) => {
                  const style = GetHighlightStyle(field, scale.x, scale.y);
                  const isSelected = field.commonKeys.includes(selectedKey);

                  return (
                    <div
                      key={i}
                      ref={(el) => {
                        if (el) fieldRefs.current[field.key] = el;
                      }}
                      className="absolute group cursor-pointer pointer-events-auto"
                      style={{
                        left: style.left - 4,
                        top: style.top - 4,
                        width: style.width + 8,
                        height: style.height + 8,
                      }}
                      onClick={() => onFieldClick?.(field.key)}
                    >
                      <div
                        className={`w-full h-full rounded-sm bg-yellow-200 bg-opacity-30 border-2 transition-colors duration-150 ${isSelected ? "border-indigo-500" : "border-yellow-400"
                          }`}
                      />
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                        {field.commonKeys.join(", ")}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* Arrow connection - rendered at root level with high z-index */}
      {arrowPosition && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 pointer-events-none z-[100]">
          <ArrowConnection
            startX={arrowPosition.startX}
            startY={arrowPosition.startY}
            endX={arrowPosition.endX}
            endY={arrowPosition.endY}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default ImageViewerWithHighlights;
